import BudgetAmountChangeDialog from './BudgetAmountChangeDialog';
import BudgetAmountHistory, { ScheduledBudgetAmount } from './BudgetAmountHistory';
import { getBudgetAmountAt } from '../../utils/budgetAmounts';
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useData } from '../../contexts/DataContext';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  MenuItem,
  InputLabel,
  InputAdornment,
  CircularProgress,
  Select,
  FormControl,
  Tooltip,
  styled
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { exportBudgetsToCSV } from '../../utils/budgetsExportUtils';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import BudgetImportDialog from './BudgetImportDialog';
import BudgetMatrixView from './BudgetMatrixView';
import AiBudgetRecommendationsDialog from './AiBudgetRecommendationsDialog';
import api from '../../services/api';
import { useScrollableFilters } from '../Rules/hooks/useScrollableFilters';
import ScrollableFilters from '../Rules/components/ScrollableFilters';
import ListFooter from '../common/ListFooter';
import ListSection from '../common/ListSection';
import AppDialog, { DialogField } from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import CategoryChip from '../common/CategoryChip';
import StatusChip from '../common/StatusChip';
import ToolbarButton from '../common/ToolbarButton';
import ResponsiveListTable from '../common/ResponsiveListTable';

// Larger page sizes now that rows are one line tall
const ROWS_OPTIONS = [10, 25, 50];

// Shared styling for the inline filter fields (white bg so they sit cleanly on the card),
// matching the Transactions filter bar.
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

// Budget dates can arrive as ISO datetimes or Date objects, but a type="date"
// input only accepts yyyy-MM-dd — normalise so editing a budget pre-fills the
// dates instead of showing an empty field.
const toDateInputValue = (v) => {
  if (!v) return '';
  const s = String(v);
  const m = s.match(/^\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

// Filter toggle button style copied from RulesTable to match visual style
const FilterToggleButton = styled(Button)(({ theme, selected }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  height: 36,
  padding: '6px 16px',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  border: '1px solid #d1d5db',
  minWidth: 'auto',
  boxShadow: 'none',
  ...(selected ? {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
    }
  } : {
    backgroundColor: '#f9fafb',
    color: '#374151',
    borderColor: '#d1d5db',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#9ca3af',
      transform: 'translateY(-1px)',
    }
  })
}));

const EnhancedBudgetManager = ({ layoutMode: initialLayoutMode = 'list', setLayoutMode: parentSetLayoutMode = null }) => {
  const { categories, budgets, fetchBudgets, addBudget, updateBudget, deleteBudget, accounts } = useData();
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('budg_sortBy') || 'name');
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem('budg_sortDir') || 'asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [amountChangeOpen, setAmountChangeOpen] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [layoutMode, setLayoutMode] = useState(initialLayoutMode); // 'list' | 'matrix'
  const [budgetsCategoryFilter, setBudgetsCategoryFilter] = useState('');
  const budgetsFiltersScroll = useScrollableFilters();
  // If parent controls layoutMode via props, sync local state to prop
  useEffect(() => {
    setLayoutMode(initialLayoutMode);
  }, [initialLayoutMode]);
  const currentYear = new Date().getFullYear();
  const [matrixYear, setMatrixYear] = useState(currentYear);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = parseInt(localStorage.getItem('budg_rowsPerPage') || '25', 10);
    return ROWS_OPTIONS.includes(stored) ? stored : 25;
  });
  // Selection state for bulk actions
  const [selectedBudgetIds, setSelectedBudgetIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewBudget, setViewBudget] = useState(null);
  const [importMessage, setImportMessage] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  // AI budget review: null = dialog closed, [] = open but all handled
  const [aiRecommending, setAiRecommending] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    categoryId: '',
    accountId: '',
    amount: '',
    type: 'Expense',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isRecurring: false,
  isProrated: false,
    recurringPeriod: 'monthly',
    name: '',
    notes: '',
    isActive: true
  });

  // Load budgets
  useEffect(() => {
    const loadBudgets = async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchBudgets();
      } catch (error) {
        setError('Failed to load budgets. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    loadBudgets();
  }, [fetchBudgets]);

  // Trigger scroll button check when categories change
  useEffect(() => {
    // Delay to ensure DOM has rendered
    const timer = setTimeout(() => {
      budgetsFiltersScroll.checkScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [categories, budgetsFiltersScroll]);

  const renderCategoryFilters = () => (
    <Box sx={{ px: 3, pt: 1, pb: 1 }}>
      <ScrollableFilters
        scrollRef={budgetsFiltersScroll.scrollRef}
        showLeftScroll={budgetsFiltersScroll.showLeftScroll}
        showRightScroll={budgetsFiltersScroll.showRightScroll}
        onScrollLeft={budgetsFiltersScroll.scrollLeft}
        onScrollRight={budgetsFiltersScroll.scrollRight}
        sx={{ width: '100%' }}
      >
        <FilterToggleButton
          selected={budgetsCategoryFilter === ''}
          onClick={() => setBudgetsCategoryFilter('')}
          sx={{ typography: 'body3' }}
        >
          All ({budgets.length})
        </FilterToggleButton>
        {categories.filter(c => !c.isTransfer).map(cat => (
          <FilterToggleButton
            key={cat.id}
            selected={budgetsCategoryFilter === cat.id}
            onClick={() => setBudgetsCategoryFilter(cat.id)}
            sx={{ typography: 'body3' }}
          >
            {cat.name}
          </FilterToggleButton>
        ))}
      </ScrollableFilters>
    </Box>
  );

  const getRecurringLabel = useCallback((period) => {
    const labels = {
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels[period] || period;
  }, []);

  // ---------- Utility Getters & Formatters (restored) ----------
  const getCategoryName = useCallback((categoryId) => {
    if (!categoryId) return '';
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : '';
  }, [categories]);

  const getCategoryColor = useCallback((categoryId) => {
    if (!categoryId) return '#9e9e9e';
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#9e9e9e';
  }, [categories]);

  const getAccountName = useCallback((accountId) => {
    if (!accountId) return 'All Accounts';
    const acc = accounts.find(a => a.id === accountId);
    return acc ? acc.name : 'All Accounts';
  }, [accounts]);

  const formatCurrency = useCallback((value) => {
    if (value === undefined || value === null || value === '') return '$0.00';
    const num = Number(value) || 0;
    return '$' + num.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }, []);

  // ---------- Dialog & Form Handlers (restored) ----------
  const openDialog = useCallback((budget = null) => {
    setEditingBudget(budget);
    setAmountChangeOpen(false);
    if (budget) {
      setEditMode(budget.id);
      setFormData({
        id: budget.id,
        categoryId: budget.categoryId || '',
        accountId: budget.accountId || '',
        amount: getBudgetAmountAt(budget),
        type: budget.type || 'Expense',
        startDate: toDateInputValue(budget.startDate) || new Date().toISOString().split('T')[0],
        endDate: toDateInputValue(budget.endDate),
        isRecurring: !!budget.isRecurring,
        isProrated: !!budget.isProrated,
        recurringPeriod: budget.recurringPeriod || 'monthly',
        name: budget.name || '',
        notes: budget.notes || '',
        isActive: budget.isActive !== false
      });
    } else {
      setEditMode(null);
      setFormData(prev => ({
        ...prev,
        id: '',
        categoryId: budgetsCategoryFilter || '',
        accountId: '',
        amount: '',
        type: 'Expense',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isRecurring: false,
        isProrated: false,
        recurringPeriod: 'monthly',
        name: '',
        notes: '',
        isActive: true
      }));
    }
    setFormError('');
    setDialogOpen(true);
  }, [budgetsCategoryFilter]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setAmountChangeOpen(false);
    setTimeout(() => {
      setEditMode(null);
    }, 200);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'amount' ? value : value)
    }));
  }, []);

  const validateForm = useCallback(() => {
    if (!formData.categoryId) return 'Category is required';
    if (formData.amount === '' || !Number.isFinite(Number(formData.amount)) || Number(formData.amount) < 0) return 'Valid amount is required';
    if (!formData.startDate) return 'Start date is required';
    if (formData.endDate && formData.endDate < formData.startDate) return 'End date must be on or after the start date';
    return '';
  }, [formData]);

  const handleSubmit = useCallback(async (amountChange = null) => {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    if (savingBudget) return;
    const amountChanged = editingBudget && Number(formData.amount) !== getBudgetAmountAt(editingBudget);
    if (amountChanged && editingBudget.isRecurring && !amountChange) {
      const scheduleChanged = ['startDate', 'endDate', 'isRecurring', 'isProrated', 'recurringPeriod'].some(key =>
        key.endsWith('Date') ? formData[key] !== toDateInputValue(editingBudget[key]) : formData[key] !== editingBudget[key]);
      if (scheduleChanged) { setFormError('Save amount changes separately from schedule changes.'); return; }
      setFormError('');
      setAmountChangeOpen(true);
      return;
    }
    setSavingBudget(true);
    setFormError('');
    try {
      const payload = {
        ...(amountChange ? { amountChange } : {}),
        ...(editingBudget?.updatedAt ? { expectedUpdatedAt: editingBudget.updatedAt } : {}),
        id: editMode || uuidv4(),
        categoryId: formData.categoryId,
        accountId: formData.accountId || '',
        amount: Number(formData.amount) || 0,
        type: formData.type,
        startDate: formData.startDate,
        endDate: formData.endDate || '',
        isRecurring: formData.isRecurring,
        isProrated: formData.isProrated,
        recurringPeriod: formData.recurringPeriod,
        name: formData.name.trim(),
        notes: formData.notes.trim(),
        isActive: formData.isActive !== false
      };
      if (editMode && !amountChanged && !amountChange) delete payload.amount;
      if (editMode) {
        await updateBudget(payload.id, payload);
      } else {
        await addBudget(payload);
      }
      closeDialog();
    } catch (e) {
      setFormError(e.message || 'Failed to save budget. Please try again.');
    } finally {
      setSavingBudget(false);
    }
  }, [formData, editMode, editingBudget, savingBudget, addBudget, updateBudget, closeDialog, validateForm]);

  const handleDeleteClick = useCallback((budget) => {
    setBudgetToDelete(budget);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!budgetToDelete) return;
    try {
      await deleteBudget(budgetToDelete.id);
      setDeleteDialogOpen(false);
      setBudgetToDelete(null);
      setSelectedBudgetIds(prev => prev.filter(id => id !== budgetToDelete.id));
    } catch (e) {
      // Optionally set an error state/toast
    }
  }, [budgetToDelete, deleteBudget]);

  const toggleSelectBudget = useCallback((id) => {
    setSelectedBudgetIds(prev => prev.includes(id) ? prev.filter(bid => bid !== id) : [...prev, id]);
  }, []);

  const handleOpenBulkDelete = useCallback(() => {
    if (selectedBudgetIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  }, [selectedBudgetIds]);

  const handleConfirmBulkDelete = useCallback(async () => {
    try {
      for (const id of selectedBudgetIds) {
        await deleteBudget(id);
      }
      setSelectedBudgetIds([]);
    } catch (e) {
      // Optionally handle error (toast)
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  }, [selectedBudgetIds, deleteBudget]);

  const handleQuickAdd = useCallback(() => {
    openDialog();
  }, [openDialog]);

  const handleImportBudgets = useCallback(async (importedBudgets) => {
    setImportLoading(true);
    try {
      for (const b of importedBudgets) {
        await addBudget({ ...b, id: uuidv4() });
      }
      setImportMessage({ type: 'success', text: `Successfully imported ${importedBudgets.length} budget(s)` });
      setTimeout(() => setImportMessage(null), 5000);
    } catch (err) {
      setImportMessage({ type: 'error', text: err.message || 'Failed to import budgets' });
      setTimeout(() => setImportMessage(null), 5000);
      throw err;
    } finally {
      setImportLoading(false);
    }
  }, [addBudget]);

  // Ask the backend to review budgets against the last 6 months of
  // category activity. Opens the review dialog when there are proposals.
  const handleAiReview = useCallback(async () => {
    setAiRecommending(true);
    try {
      const { data } = await api.post('/ai/budget-recommendations');
      if (!data.recommendations.length) {
        setImportMessage({
          type: 'info',
          text: `AI reviewed ${data.reviewed.budgets} budget(s) and ${data.reviewed.categories} categories against the last 6 months. No changes recommended.`
        });
        setTimeout(() => setImportMessage(null), 8000);
      } else {
        setAiRecommendations(data.recommendations);
      }
    } catch (error) {
      setImportMessage({
        type: 'error',
        text: error.response?.data?.error || 'AI budget review failed'
      });
      setTimeout(() => setImportMessage(null), 8000);
    } finally {
      setAiRecommending(false);
    }
  }, []);

  const handleApplyAiRecommendation = useCallback(async (rec) => {
    try {
      if (rec.action === 'modify') {
        const budget = budgets.find(b => b.id === rec.budgetId);
        if (!budget) throw new Error('Budget not found');
        openDialog(budget);
        setFormData(prev => ({ ...prev, amount: rec.suggestedAmount }));
        setAiRecommendations(null);
        return;
      } else {
        await addBudget({
          id: uuidv4(),
          categoryId: rec.categoryId,
          accountId: '',
          amount: rec.suggestedAmount,
          type: rec.type,
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          isRecurring: true,
          isProrated: false,
          recurringPeriod: rec.recurringPeriod || 'monthly',
          name: '',
          notes: rec.rationale,
          isActive: true
        });
        setImportMessage({
          type: 'success',
          text: `Created a ${rec.recurringPeriod || 'monthly'} budget for ${rec.categoryName}`
        });
      }
      setAiRecommendations(prev => (prev || []).filter(r => r !== rec));
      setTimeout(() => setImportMessage(null), 5000);
    } catch (e) {
      setImportMessage({ type: 'error', text: 'Failed to apply recommendation' });
      setTimeout(() => setImportMessage(null), 5000);
    }
  }, [budgets, openDialog, addBudget]);

  const handleDismissAiRecommendation = useCallback((rec) => {
    setAiRecommendations(prev => (prev || []).filter(r => r !== rec));
  }, []);

  const handleCloseAiRecommendations = useCallback(() => setAiRecommendations(null), []);

  // Filter and sort budgets
  const filteredBudgets = useMemo(() => {
    return budgets
      .filter(budget => {
        // Search filter
        const budgetName = budget.name || getCategoryName(budget.categoryId);
        const searchMatch = !searchTerm || 
          budgetName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          getCategoryName(budget.categoryId).toLowerCase().includes(searchTerm.toLowerCase());
        
        // Type filter
        const typeMatch = filterType === 'all' || 
          (filterType === 'active' && budget.isActive) || 
          (filterType === 'inactive' && !budget.isActive) ||
          (filterType === 'income' && budget.type === 'Income') ||
          (filterType === 'expense' && budget.type === 'Expense');
        
        return searchMatch && typeMatch;
      })
      // Category filter
      .filter(budget => {
        return !budgetsCategoryFilter || budget.categoryId === budgetsCategoryFilter;
      })
      .sort((a, b) => {
        // Sort active budgets first — unless the user is sorting by status,
        // where this pre-sort would swallow the chosen direction
        if (sortBy !== 'status' && a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }

        // Then apply user selected sort
        const getKey = (item) => {
          if (sortBy === 'amount') return getBudgetAmountAt(item);
          if (sortBy === 'category') return getCategoryName(item.categoryId) || '';
          if (sortBy === 'frequency') return item.isRecurring ? getRecurringLabel(item.recurringPeriod) : 'One-time';
          if (sortBy === 'account') return getAccountName(item.accountId) || '';
          if (sortBy === 'status') return item.isActive ? 0 : 1;
          return item.name || getCategoryName(item.categoryId) || '';
        };

        const aKey = getKey(a);
        const bKey = getKey(b);

        if (typeof aKey === 'number' && typeof bKey === 'number') {
          return sortDirection === 'asc' ? aKey - bKey : bKey - aKey;
        }

        return sortDirection === 'asc' ? String(aKey).localeCompare(String(bKey)) : String(bKey).localeCompare(String(aKey));
      });
  }, [budgets, getCategoryName, getAccountName, getRecurringLabel, searchTerm, filterType, budgetsCategoryFilter, sortBy, sortDirection]);

  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      setSelectedBudgetIds(filteredBudgets.map(b => b.id));
    } else {
      setSelectedBudgetIds([]);
    }
  }, [filteredBudgets]);

  // Column-header sorting (desktop table)
  const handleSort = useCallback((column) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    const dir = isAsc ? 'desc' : 'asc';
    setSortBy(column);
    setSortDirection(dir);
    localStorage.setItem('budg_sortBy', column);
    localStorage.setItem('budg_sortDir', dir);
    setPage(0);
  }, [sortBy, sortDirection]);

  const handleExportBudgets = useCallback(() => {
    try {
      const toExport = selectedBudgetIds.length > 0
        ? budgets.filter(b => selectedBudgetIds.includes(b.id))
        : filteredBudgets;
      exportBudgetsToCSV(toExport, categories, accounts);
      setImportMessage({ type: 'success', text: 'Budgets exported successfully' });
      setTimeout(() => setImportMessage(null), 3000);
    } catch (error) {
      setImportMessage({ type: 'error', text: 'Failed to export budgets' });
      setTimeout(() => setImportMessage(null), 3000);
    }
  }, [budgets, filteredBudgets, selectedBudgetIds, categories, accounts]);

  // Paginated budgets
  const paginatedBudgets = useMemo(() => {
    return filteredBudgets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredBudgets, page, rowsPerPage]);

  // Render empty state
  const renderEmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 8, px: 4, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
        No budgets found
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {searchTerm || filterType !== 'all' 
          ? "Try changing your search or filter criteria" 
          : "Create your first budget to start tracking your finances"}
      </Typography>
      {/* Add action is available in the Budgets section header */}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', mb: 4 }}>
      {/* Matrix View */}
      {layoutMode === 'matrix' && (
        <BudgetMatrixView
          budgets={budgets}
          categories={categories}
          year={matrixYear}
          onYearChange={setMatrixYear}
          filterType={filterType}
            searchTerm={searchTerm}
          getCategoryName={getCategoryName}
          onEditBudget={openDialog}
          onDeleteBudget={handleDeleteClick}
          onAddBudget={handleQuickAdd}
        />
      )}

      {/* List Table */}
      {layoutMode === 'list' && (loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : (
        <Paper elevation={2} sx={{ p: 0, borderRadius: 3, width: '100%', maxWidth: '100%', mb: 4 }}>
          <Box sx={{
            px: 3,
            pt: 1.5,
            pb: 1.5,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}>
            {/* Buttons on the left */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', flexShrink: 0 }}>
              <ToolbarButton startIcon={<AddIcon />} onClick={() => openDialog()}>
                Add
              </ToolbarButton>
              <Tooltip title="Export budgets to CSV">
                <IconButton
                  onClick={handleExportBudgets}
                  disabled={filteredBudgets.length === 0}
                  sx={{ color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' }, '&.Mui-disabled': { bgcolor: '#f8bbd0', color: '#fff' } }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Import budgets from CSV">
                <IconButton
                  onClick={() => setImportDialogOpen(true)}
                  sx={{ color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' } }}
                >
                  <FileUploadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Real inline box (not display:contents) so the tooltip can
                  anchor to the button even while it's disabled */}
              <Tooltip title="AI reviews your budgets against the last 6 months of category activity and suggests adjustments or new budgets for you to approve.">
                <span style={{ display: 'inline-flex' }}>
                  <ToolbarButton
                    color="secondary"
                    startIcon={aiRecommending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
                    onClick={handleAiReview}
                    disabled={aiRecommending}
                  >
                    {aiRecommending ? 'Thinking...' : 'AI Review'}
                  </ToolbarButton>
                </span>
              </Tooltip>
            </Box>
            
            {/* Filters on the right (Transactions-style filter bar) */}
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <ScrollableFilters
                scrollRef={budgetsFiltersScroll.scrollRef}
                showLeftScroll={budgetsFiltersScroll.showLeftScroll}
                showRightScroll={budgetsFiltersScroll.showRightScroll}
                onScrollLeft={budgetsFiltersScroll.scrollLeft}
                onScrollRight={budgetsFiltersScroll.scrollRight}
              >
                <TextField
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  size="small"
                  sx={{ minWidth: 200, ...filterFieldSx }}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                    style: { fontSize: '0.875rem' }
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                  <InputLabel id="budg-filter-category-label" shrink>Category</InputLabel>
                  <Select
                    labelId="budg-filter-category-label"
                    value={budgetsCategoryFilter}
                    label="Category"
                    onChange={(e) => { setBudgetsCategoryFilter(e.target.value); setPage(0); }}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Categories</MenuItem>
                    {categories.filter(c => !c.isTransfer).map(cat => (
                      <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.875rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: getCategoryColor(cat.id) }} />
                          {cat.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, ...filterFieldSx }}>
                  <InputLabel id="budg-filter-status-label" shrink>Status</InputLabel>
                  <Select
                    labelId="budg-filter-status-label"
                    value={filterType}
                    label="Status"
                    onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                    <MenuItem value="active" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                    <MenuItem value="inactive" sx={{ fontSize: '0.875rem' }}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </ScrollableFilters>
            </Box>
          </Box>

          {importMessage && (
            <Box sx={{ px: 3, pt: 1 }}>
              <Alert severity={importMessage.type} onClose={() => setImportMessage(null)} sx={{ borderRadius: 2 }}>
                {importMessage.text}
              </Alert>
            </Box>
          )}

          {/* Showing and Sort controls (placed under the horizontal filters to match Transactions) */}
          {filteredBudgets.length === 0 ? (
            renderEmptyState()
          ) : (
          <Box sx={{ mb: 2, px: 3, pt: 0, pb: 3 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 28 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                Showing {filteredBudgets.length} budget{filteredBudgets.length !== 1 ? 's' : ''}
              </Typography>
              {selectedBudgetIds.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenBulkDelete}
                  color="error"
                  sx={{
                    ml: 1, borderRadius: 3, fontWeight: 500, fontSize: '0.8125rem',
                    px: 1.5, py: 0.25, minHeight: 0
                  }}
                >
                  Delete ({selectedBudgetIds.length})
                </Button>
              )}
              {/* On mobile the table headers are gone, so sorting lives in a menu */}
              <Box sx={{ ml: 'auto', display: { xs: 'block', sm: 'none' } }}>
                <Button size="small" startIcon={<SortIcon />} onClick={(e) => setSortMenuAnchor(e.currentTarget)} sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 500 }}>
                  Sort by
                </Button>
                <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)} keepMounted>
                  {[['name', 'Name'], ['amount', 'Amount'], ['category', 'Category'], ['frequency', 'Frequency'], ['account', 'Account'], ['status', 'Status']].flatMap(([id, label]) => (
                    ['asc', 'desc'].map(dir => (
                      <MenuItem
                        key={`${id}-${dir}`}
                        sx={{ fontSize: '0.875rem' }}
                        onClick={() => { setSortBy(id); setSortDirection(dir); localStorage.setItem('budg_sortBy', id); localStorage.setItem('budg_sortDir', dir); setPage(0); setSortMenuAnchor(null); }}
                      >
                        {label} {dir === 'asc' ? '↑' : '↓'}
                      </MenuItem>
                    ))
                  ))}
                </Menu>
              </Box>
            </Box>

            <ResponsiveListTable
              items={paginatedBudgets}
              getId={(b) => b.id}
              columns={[
                { id: 'name', label: 'Name', render: b => b.name || getCategoryName(b.categoryId), sx: { fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' } },
                {
                  id: 'category', label: 'Category',
                  render: b => (
                    <CategoryChip
                      label={getCategoryName(b.categoryId)}
                      color={getCategoryColor(b.categoryId)}
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  )
                },
                {
                  id: 'frequency', label: 'Frequency',
                  render: b => `${b.isRecurring ? getRecurringLabel(b.recurringPeriod) : 'One-time'}${b.endDate ? ` (to ${formatDate(b.endDate)})` : ''}`,
                  sx: { color: 'text.secondary' }
                },
                { id: 'account', label: 'Account', render: b => getAccountName(b.accountId), sx: { color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' } },
                {
                  id: 'status', label: 'Status',
                  render: b => (
                    <StatusChip
                      status={b.isActive ? 'success' : 'neutral'}
                      label={b.isActive ? 'Active' : 'Inactive'}
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  )
                },
                {
                  id: 'amount', label: 'Amount', align: 'right',
                  render: b => (
                    <Box component="span" sx={{ fontWeight: 700 }}>{formatCurrency(getBudgetAmountAt(b))}<ScheduledBudgetAmount budget={b} /></Box>
                  )
                }
              ]}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              selectedIds={selectedBudgetIds}
              onToggleSelect={toggleSelectBudget}
              onSelectAll={(checked) => handleSelectAllClick({ target: { checked } })}
              actions={[
                { key: 'view', label: 'View', icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />, onClick: (b) => { setViewBudget(b); setViewDialogOpen(true); } },
                { key: 'edit', label: 'Edit', icon: <EditIcon sx={{ fontSize: 18 }} />, onClick: (b) => openDialog(b) },
                { key: 'delete', label: 'Delete', icon: <DeleteIcon sx={{ fontSize: 18 }} />, onClick: handleDeleteClick, danger: true }
              ]}
              onRowClick={(b) => { setViewBudget(b); setViewDialogOpen(true); }}
              mobilePrimary={(b) => b.name || getCategoryName(b.categoryId)}
              mobileSecondary={(b) => `${b.isRecurring ? getRecurringLabel(b.recurringPeriod) : 'One-time'} • ${getAccountName(b.accountId)}`}
              mobileChips={(b) => (
                <>
                  <CategoryChip
                    label={getCategoryName(b.categoryId)}
                    color={getCategoryColor(b.categoryId)}
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                  <StatusChip
                    status={b.isActive ? 'success' : 'neutral'}
                    label={b.isActive ? 'Active' : 'Inactive'}
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                </>
              )}
              mobileAmount={(b) => (
                <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  {formatCurrency(getBudgetAmountAt(b))}<ScheduledBudgetAmount budget={b} />
                </Typography>
              )}
            />

            <ListFooter
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={(val) => {
                setRowsPerPage(val);
                localStorage.setItem('budg_rowsPerPage', String(val));
                setPage(0);
              }}
              totalCount={filteredBudgets.length}
              rowsOptions={ROWS_OPTIONS}
            />
          </Box>
          )}
        </Paper>
      ))}
      
      {/* Add/Edit Dialog */}
      <AppDialog
        open={dialogOpen && !amountChangeOpen}
        onClose={savingBudget ? undefined : closeDialog}
        title={editMode ? 'Edit Budget' : 'Add Budget'}
        subtitle="Set a spending or income target"
        primaryLabel={savingBudget ? 'Saving…' : editMode ? 'Save' : 'Add'}
        primaryDisabled={savingBudget}
        onPrimary={() => handleSubmit()}
      >
        {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
        <DialogField label="Name">
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., Monthly Groceries Budget"
            size="small"
            autoFocus
          />
        </DialogField>
        <DialogField label="Category" required>
          <FormControl fullWidth size="small">
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <Box component="span" sx={{ color: 'text.secondary' }}>Select a category</Box>;
                const cat = categories.find(c => c.id === selected);
                return cat ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || '#9e9e9e', mr: 1 }} />
                    {cat.name}
                  </Box>
                ) : null;
              }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: 'text.secondary' }}>Select a category</Box>
              </MenuItem>
              {categories.filter(cat => !cat.isTransfer).map(category => (
                <MenuItem key={category.id} value={category.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: category.color || '#9e9e9e', mr: 1 }} />
                    {category.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>
        <DialogField label="Account">
          <FormControl fullWidth size="small">
            <Select
              name="accountId"
              value={formData.accountId}
              onChange={handleInputChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <Box component="span" sx={{ color: 'text.secondary' }}>All Accounts</Box>;
                const acc = accounts.find(a => a.id === selected);
                return acc ? acc.name : 'All Accounts';
              }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: 'text.secondary' }}>All Accounts</Box>
              </MenuItem>
              {accounts.filter(acc => acc.isActive).map(account => (
                <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>
        {/* Amount + Type share a row — "how much, and is it spending or income". */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DialogField label="Amount" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              size="small"
              InputProps={{ startAdornment: (<InputAdornment position="start">$</InputAdornment>) }}
            />
          </DialogField>
          <DialogField label="Type" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <Select name="type" value={formData.type} onChange={handleInputChange}>
                <MenuItem value="Expense">Expense</MenuItem>
                <MenuItem value="Income">Income</MenuItem>
              </Select>
            </FormControl>
          </DialogField>
        </Box>
        {editingBudget && <BudgetAmountHistory budget={editingBudget} />}
        {!!editingBudget?.amountHistory?.length && <Alert severity="info" sx={{ mt: 2 }}>This budget has dated amounts. Its schedule is kept fixed to preserve that history.</Alert>}
        {/* Start + End dates share a row. */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DialogField label="Start Date" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              name="startDate"
              disabled={!!editingBudget?.amountHistory?.length}
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </DialogField>
          <DialogField label="End Date" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              name="endDate"
              disabled={!!editingBudget?.amountHistory?.length}
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </DialogField>
        </Box>
        <DialogField>
          <FormControlLabel
            control={<Checkbox size="small" disabled={!!editingBudget?.amountHistory?.length} checked={formData.isRecurring} onChange={handleInputChange} name="isRecurring" />}
            label={<Typography variant="body2">Recurring</Typography>}
          />
        </DialogField>
        {formData.isRecurring && (
          <DialogField label="Period">
            <FormControl fullWidth size="small">
              <Select disabled={!!editingBudget?.amountHistory?.length} name="recurringPeriod" value={formData.recurringPeriod} onChange={handleInputChange} displayEmpty>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </DialogField>
        )}
        {formData.isRecurring && (
          <DialogField helper="Spread the budget evenly across the selected period.">
            <FormControlLabel
              control={<Checkbox size="small" disabled={!!editingBudget?.amountHistory?.length} checked={formData.isProrated} onChange={handleInputChange} name="isProrated" />}
              label={<Typography variant="body2">Prorate</Typography>}
            />
          </DialogField>
        )}
        <DialogField label="Notes">
          <TextField
            fullWidth
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            size="small"
            multiline
            rows={2}
            placeholder="Add any additional notes..."
          />
        </DialogField>
        <DialogField>
          <FormControlLabel
            control={<Checkbox size="small" checked={formData.isActive} onChange={handleInputChange} name="isActive" />}
            label={<Typography variant="body2">Active</Typography>}
          />
        </DialogField>
      </AppDialog>
      
      {amountChangeOpen && editingBudget && <BudgetAmountChangeDialog
        budget={editingBudget} amount={Number(formData.amount)} saving={savingBudget} error={formError}
        onClose={() => { setAmountChangeOpen(false); setFormError(''); }} onSave={handleSubmit}
      />}
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Budget"
        message={`Are you sure you want to delete the budget${budgetToDelete?.name ? ` "${budgetToDelete.name}"` : ''}? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />

      {/* View Budget Details Dialog (read-only) */}
      <AppDialog
        open={viewDialogOpen}
        onClose={() => { setViewDialogOpen(false); setViewBudget(null); }}
        title="Budget Details"
      >
        {viewBudget && (
          <>
            <DialogField label="Name">
              <Typography variant="body2">{viewBudget.name || '—'}</Typography>
            </DialogField>
            <DialogField label="Category">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: getCategoryColor(viewBudget.categoryId) }} />
                <Typography variant="body2">{getCategoryName(viewBudget.categoryId) || '—'}</Typography>
              </Box>
            </DialogField>
            <DialogField label="Account">
              <Typography variant="body2">{getAccountName(viewBudget.accountId) || 'All Accounts'}</Typography>
            </DialogField>
            <DialogField label="Amount">
              <Typography variant="body2">{formatCurrency(getBudgetAmountAt(viewBudget))}</Typography>
            </DialogField>
            <BudgetAmountHistory budget={viewBudget} />
            <DialogField label="Type">
              <Typography variant="body2">{viewBudget.type || '—'}</Typography>
            </DialogField>
            <DialogField label="Start Date">
              <Typography variant="body2">{viewBudget.startDate ? formatDate(viewBudget.startDate) : '—'}</Typography>
            </DialogField>
            <DialogField label="End Date">
              <Typography variant="body2">{viewBudget.endDate ? formatDate(viewBudget.endDate) : '—'}</Typography>
            </DialogField>
            <DialogField label="Recurring">
              <Typography variant="body2">{viewBudget.isRecurring ? 'Yes' : 'No'}</Typography>
            </DialogField>
            {viewBudget.isRecurring && (
              <>
                <DialogField label="Period">
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{viewBudget.recurringPeriod || '—'}</Typography>
                </DialogField>
                <DialogField label="Prorate">
                  <Typography variant="body2">{viewBudget.isProrated ? 'Yes' : 'No'}</Typography>
                </DialogField>
              </>
            )}
            <DialogField label="Notes">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewBudget.notes || '—'}</Typography>
            </DialogField>
            <DialogField label="Active">
              <Chip
                label={viewBudget.isActive !== false ? 'Active' : 'Inactive'}
                size="small"
                color={viewBudget.isActive !== false ? 'success' : 'default'}
                sx={{ fontWeight: 500 }}
              />
            </DialogField>
          </>
        )}
      </AppDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        title={`Delete ${selectedBudgetIds.length} budget${selectedBudgetIds.length !== 1 ? 's' : ''}?`}
        message="This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleConfirmBulkDelete}
      />

      {/* Import Dialog */}
      <BudgetImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        categories={categories}
        accounts={accounts}
        onImport={handleImportBudgets}
        isLoading={importLoading}
      />

      {/* AI Budget Recommendations review dialog */}
      <AiBudgetRecommendationsDialog
        recommendations={aiRecommendations}
        onApply={handleApplyAiRecommendation}
        onDismiss={handleDismissAiRecommendation}
        onClose={handleCloseAiRecommendations}
        formatCurrency={formatCurrency}
        getCategoryColor={getCategoryColor}
      />
    </Box>
  );
};

export default EnhancedBudgetManager;
